/**
 * main.ts — the caregiver invite lander (ENG-417, spec 2026-09-03 section 5.4).
 *
 * The browser destination for a shared `suvi.health/invite/<code>` link (per-env
 * `<zone>/invite/<code>`). A device WITH the app installed never sees this page —
 * the universal link / App Link opens redemption directly. This page is an
 * INSTALL INTERSTITIAL ONLY: it validates the code's SHAPE client-side (no
 * network call, ever — every server deny would otherwise be an enumeration
 * oracle for an unauthenticated visitor), explains the two steps, and sends the
 * visitor to the store. It collects no email, no name, and starts no session:
 * authentication is Apple/Google native OAuth INSIDE the app (spec 5.3), never
 * on the web. A visit here never spends the token — only the in-app claim
 * (`POST /v1/invitations/{id}/claim`, apps/mobile) does that.
 *
 * State survives the install gap via, in the spec's priority order (5.4): the
 * SMS link re-tapped after install; the Android Play Install Referrer (link
 * emitted here, not yet consumed by the app — investigation #3, a post-pilot
 * fallback); iOS clipboard best effort (handled in-app, not here); and the
 * manual invite code, shown below with a copy button so the visitor can paste
 * it into the app if the deep link does not re-fire after install.
 */
import { setupAndroidInstallBanner } from "./android-install-banner.ts";
import { env } from "./env.ts";
import { inviteCodeFromPathname, type ParsedInviteCode } from "./invite-link.ts";
import { inviteQrSvg } from "./invite-qr.ts";
import { appHandoffMarkup } from "./native-app-handoff.ts";

/** Current per-tenant default (spec 5.8/11.1). Not yet dynamic: per-tenant
 *  configurability is a separate, later stream (implementation plan issue 5,
 *  `services/identity-persona/src/app.ts` TTL). Update this constant in step
 *  with that change so the copy never claims a value the backend does not
 *  enforce. */
const INVITE_EXPIRY_HOURS = 24;

const app = document.getElementById("app");
if (!(app instanceof HTMLElement)) throw new Error("invite-lander: missing #app mount");
const mount: HTMLElement = app;
const nativeAppStorePromotionEnabled =
  import.meta.env.VITE_NATIVE_APP_STORE_PROMOTION_ENABLED === "true";

if (nativeAppStorePromotionEnabled) {
  let bannerStorage: Storage | undefined;
  try {
    bannerStorage = window.sessionStorage;
  } catch {
    // Privacy modes can deny storage; the prompt remains dismissible for this page.
  }
  setupAndroidInstallBanner(
    {
      enabled: env.androidInstallBannerEnabled,
      androidPlayStoreUrl: env.androidPlayStoreUrl,
      hostname: window.location.hostname,
      userAgent: navigator.userAgent,
      maxTouchPoints: navigator.maxTouchPoints ?? 0,
      storage: bannerStorage,
    },
    document,
  );
}

/** Escape text interpolated into templates. Every value on this page is either
 *  a literal or a regex-validated invite-code half (invite-link.ts), so this is
 *  defense in depth rather than a live threat, matching the posture the page
 *  already held before this rewrite. Covers the OWASP set for element content
 *  and quoted attribute values. */
const HTML_ESCAPES: Readonly<Record<string, string>> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => HTML_ESCAPES[character] ?? character);
}

const BRAND_HEADER = `
  <header class="brand">
    <img class="brand__logo" src="/suvi-logomark.png" width="26" height="33" alt="" aria-hidden="true" />
    <span class="brand__name">Suvi Health</span>
  </header>
`;

/** The one generic terminal state for EVERY invalid outcome — malformed link,
 *  unknown code, expired, already used, revoked. Deliberately indistinguishable
 *  (spec 5.9): the server is never consulted, so this state covers a
 *  client-side shape failure exactly the same as a code that was once valid. */
function renderInvalid(): void {
  mount.innerHTML = `
    ${BRAND_HEADER}
    <h1>This invite link isn't available</h1>
    <p>
      The link may have expired, already been used, or been copied incompletely.
      Ask the person who invited you to send a new one from their Suvi Health app.
    </p>
  `;
}

function inviteCodeBlockMarkup(rawCode: string): string {
  return `
    <div class="invite-code" role="group" aria-label="Invite code">
      <span class="invite-code__value" id="invite-code-value">${escapeHtml(rawCode)}</span>
      <button type="button" id="invite-code-copy" class="invite-code__copy">Copy</button>
    </div>
  `;
}

function wireInviteCodeCopy(rawCode: string): void {
  const button = mount.querySelector<HTMLButtonElement>("#invite-code-copy");
  if (!button) return;
  // Captured once, outside the handler: reading `button.textContent` inside
  // the click handler races a second click within the 2s window — if the
  // first click's "Copied" label is already showing when the second click's
  // handler reads "original", the reset timeout writes "Copied" back instead
  // of "Copy" and the button latches permanently.
  const originalLabel = button.textContent;
  let pendingResetId: number | undefined;
  button.addEventListener("click", () => {
    const clipboard = navigator.clipboard;
    if (!clipboard) return;
    void clipboard
      .writeText(rawCode)
      .then(() => {
        if (pendingResetId !== undefined) window.clearTimeout(pendingResetId);
        button.textContent = "Copied";
        pendingResetId = window.setTimeout(() => {
          button.textContent = originalLabel;
          pendingResetId = undefined;
        }, 2000);
      })
      .catch(() => {
        // Clipboard access can be denied (permissions, insecure context); the
        // code stays visible to select and copy by hand either way.
      });
  });
}

/** A desktop-only affordance (hidden on narrow viewports, styles.css): scan to
 *  open this SAME invite on a phone. Encodes the page's own URL, so scanning it
 *  reaches the identical landing state a phone visitor would get from the
 *  original link. Built from the canonical origin + validated `/invite/<code>`
 *  path — NOT `window.location.href` — because a shared link can carry an
 *  attacker- or redirect-supplied query string or fragment of arbitrary
 *  length, and a QR code has a finite capacity: an oversized URL would throw
 *  out of the encoder and abort the whole landing render. `code` is already
 *  shape-validated (invite-link.ts) and both halves are fixed-length, so this
 *  URL's length is bounded regardless of what the visitor's actual address
 *  bar carries. */
function desktopQrMarkup(canonicalUrl: string): string {
  let qrSvg: string;
  try {
    qrSvg = inviteQrSvg(canonicalUrl);
  } catch {
    // Belt-and-braces: the encoder should never overflow on a bounded
    // canonical URL, but a scan affordance is optional — never let it take
    // the rest of the landing page down.
    return "";
  }
  return `
    <div class="qr">
      <p class="qr__caption">Or scan with your phone</p>
      ${qrSvg}
    </div>
  `;
}

function renderLanding(code: ParsedInviteCode): void {
  const rawCode = `${code.invitationId}.${code.token}`;
  // Canonical, NOT window.location.href: both halves are fixed-length and
  // every character in them is already URL-safe (invite-link.ts's grammar),
  // so this stays bounded no matter what query string or fragment the
  // visitor's actual address bar carries.
  const canonicalInviteUrl = `${window.location.origin}/invite/${rawCode}`;
  mount.innerHTML = `
    ${BRAND_HEADER}
    <h1>You've been invited to join a care circle on Suvi Health.</h1>
    <p>
      Someone you know wants to connect with you on Suvi Health. You will review
      and accept the invitation in the app.
    </p>
    <ol class="steps">
      <li>
        <strong>Install the Suvi Health app.</strong>
        ${appHandoffMarkup({
          hostname: window.location.hostname,
          iosAppStoreUrl: env.iosAppStoreUrl,
          androidPlayStoreUrl: env.androidPlayStoreUrl,
          // Deliberately NOT code.token: nothing in the app reads this
          // referrer yet (investigation #3 is still pending), so appending
          // the live claim token here would leak credential material to
          // Google and every URL-logging layer on that request for zero
          // present benefit. Wire a purpose-bound, one-time handoff value
          // through when the Play Install Referrer consumer actually lands.
          androidReferrerToken: "",
          userAgent: navigator.userAgent,
          maxTouchPoints: navigator.maxTouchPoints ?? 0,
          escapeHtml,
        })}
      </li>
      <li>
        <strong>Sign in to the account with the verified email or phone your invitation was sent to. Your invite will be ready to accept. You can also reopen this link.</strong>
        ${inviteCodeBlockMarkup(rawCode)}
      </li>
    </ol>
    ${desktopQrMarkup(canonicalInviteUrl)}
    <p class="notice" id="invite-expiry">
      This invite expires ${INVITE_EXPIRY_HOURS} hours after it was sent. Suvi
      Health keeps contact-addressed invitations ready for you after installation.
    </p>
  `;
  wireInviteCodeCopy(rawCode);
}

const code = inviteCodeFromPathname(window.location.pathname);
if (code) {
  renderLanding(code);
} else {
  renderInvalid();
}
